import React, { useEffect, useState, useMemo } from "react";
import {
  Row,
  Col,
  Select,
  Form,
  InputNumber,
  DatePicker,
  Grid,
  Switch,
  Typography,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  getNews /*, getSourceGenres */,
} from "../../../../Redux/Slice/NewsSlice";
import { StyledPrimaryButton } from "../../../../Components/Siderbar/styles";
import { useKeycloak } from "@react-keycloak/web";
import dayjs, { Dayjs } from "dayjs";
import type { DatePickerProps } from "antd";

const { useBreakpoint } = Grid;
const { Option } = Select;
const { Text } = Typography;

interface FilterProps {
  form: any;
  handleClose: () => void;
  searchValue: string;
  setSelectedGenres?: (genres: string[]) => void;
  selectedGenres: string[];
  newsSource?: string;
  handleNewsSourceToggle?: (checked: boolean) => void;
}

export const Filter = ({
  form,
  handleClose,
  searchValue,
  setSelectedGenres,
  selectedGenres,
  newsSource,
  handleNewsSourceToggle,
}: FilterProps) => {
  const dispatch = useDispatch();
  const screens = useBreakpoint();
  const { keycloak } = useKeycloak();

  const {
    // sourceGenres,
    // genresLoading,
    // sourceGenresLoading,
    articles: newsArticles,
    loading: newsLoading,
  } = useSelector((state: any) => state.news);

  // Static sources
  const staticSources = [
    "AFP",
    "DPA",
    "Emirates News Agency (WAM)",
    "Opta Sports Data via Reuters Connect",
    "Reuters",
    "د ب أ",
    "دبأ",
  ];

  // Static genre - only "News" for now
  const staticGenres = ["News"];

  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  // const [isGenreLoading, setIsGenreLoading] = useState(true);

  // ✅ Fetch source-genre mapping once - COMMENTED OUT FOR STATIC DATA
  // useEffect(() => {
  //   dispatch(getSourceGenres());
  // }, [dispatch]);

  // ✅ Manage loading state cleanly - COMMENTED OUT FOR STATIC DATA
  // useEffect(() => {
  //   setIsGenreLoading(genresLoading || sourceGenresLoading);
  // }, [genresLoading, sourceGenresLoading]);

  // ✅ Compute all possible genres and filtered genres based on selected sources - COMMENTED OUT FOR STATIC DATA
  // const allGenres = useMemo(
  //   () => Array.from(new Set(Object.values(sourceGenres).flat() as string[])),
  //   [sourceGenres]
  // );

  // const filteredGenres = useMemo(() => {
  //   if (!selectedSources.length) return allGenres;
  //   const selected = selectedSources.flatMap((src) => sourceGenres[src] || []);
  //   return Array.from(new Set(selected));
  // }, [selectedSources, sourceGenres, allGenres]);

  // ✅ Handle source selection
  const onSourceChange = (sources: string[]) => {
    setSelectedSources(sources);
    form.setFieldsValue({ sources, genres: [] });
    if (setSelectedGenres) setSelectedGenres([]);
  };

  // ✅ Handle genre selection
  const onGenreChange = (genres: string[]) => {
    if (setSelectedGenres) setSelectedGenres(genres);
    form.setFieldsValue({ genres });
  };

  // ✅ DatePicker with future dates disabled
  const MyDatePicker: React.FC<DatePickerProps<Dayjs>> = (props) => {
    const disableFutureDates = (current: Dayjs) =>
      current && current > dayjs().endOf("day");
    return <DatePicker {...props} disabledDate={disableFutureDates} />;
  };

  // ✅ Filter trigger
  const onFilter = (values: any) => {
    const { sources, genres, date, maxarticles } = values;

    // Format date to preserve the selected date without timezone conversion
    // Ensure we're working with local timezone date
    let formattedDate = null;
    if (date) {
      // Use startOf('day') to get the start of the selected day in local timezone
      // Then format as YYYY-MM-DD to preserve the exact date selected
      const d = dayjs(date).startOf("day");
      formattedDate = d.format("YYYY-MM-DD");
    }

    // Static genre - always use "News" for now
    const finalGenres = ["News"];

    // OLD CODE - COMMENTED OUT FOR STATIC GENRE
    // const finalGenres =
    //   searchValue && genres?.length
    //     ? genres
    //     : sources?.length
    //     ? genres
    //     : genres?.length
    //     ? genres
    //     : ["News"];

    dispatch(
      getNews({
        search: searchValue,
        sources,
        genres: finalGenres,
        date: formattedDate,
        maxarticles,
        token: keycloak.token,
      }),
    );

    handleClose?.();
  };

  return (
    <Col
      xs={24}
      lg={22}
      md={24}
      sm={24}
      style={{
        marginLeft: screens.xs ? "0px" : "10px",
        width: "100%",
      }}
    >
      <Form form={form} onFinish={onFilter}>
        <Row
          gutter={screens.xs ? [8, 8] : [16, 8]}
          align="middle"
          justify={screens.xs ? "center" : "start"}
          style={{
            width: "100%",
            margin: 0,
          }}
        >
          {/* SOURCES DROPDOWN */}
          <Col xs={12} sm={12} md={8} lg={6} xl={5}>
            <Form.Item
              name="sources"
              style={{
                marginBottom: screens.xs ? "8px" : "1px",
                width: "100%",
              }}
            >
              <Select
                mode="multiple"
                placeholder="Sources"
                maxTagCount="responsive"
                onChange={onSourceChange}
                style={{ width: "100%" }}
              >
                {staticSources
                  .sort((a, b) => a.localeCompare(b))
                  .map((source) => (
                    <Option key={source} value={source}>
                      {source}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>

          {/* GENRES DROPDOWN */}
          <Col xs={12} sm={12} md={8} lg={6} xl={5}>
            <Form.Item
              name="genres"
              style={{
                marginBottom: screens.xs ? "8px" : "1px",
                width: "100%",
              }}
            >
              <Select
                mode="multiple"
                placeholder="Genres"
                maxTagCount="responsive"
                value={selectedGenres}
                onChange={onGenreChange}
                style={{ width: "100%" }}
              >
                {staticGenres.map((genre) => (
                  <Option key={genre} value={genre}>
                    {genre}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* MAX ARTICLES INPUT */}
          <Col xs={12} sm={12} md={8} lg={4} xl={4}>
            <Form.Item
              name="maxarticles"
              style={{
                marginBottom: screens.xs ? "8px" : "1px",
                width: "100%",
              }}
            >
              <InputNumber
                min={1}
                max={10000}
                placeholder="Max Articles"
                style={{ width: "100%" }}
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) e.preventDefault();
                }}
              />
            </Form.Item>
          </Col>

          {/* DATE PICKER */}
          <Col xs={12} sm={12} md={6} lg={4} xl={4}>
            <Form.Item
              name="date"
              style={{
                marginBottom: screens.xs ? "8px" : "1px",
                width: "100%",
              }}
            >
              <MyDatePicker style={{ width: "100%" }} placeholder="Date" />
            </Form.Item>
          </Col>

          {/* APPLY BUTTON */}
          <Col xs={12} sm={12} md={6} lg={3} xl={3}>
            <StyledPrimaryButton
              type="primary"
              htmlType="submit"
              disabled={newsLoading}
              style={{
                width: "100%",
                fontSize: screens.xs ? "14px" : "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: screens.xs ? "8px" : "20px",
                height: screens.xs ? "32px" : "auto",
              }}
            >
              Apply
            </StyledPrimaryButton>
          </Col>

          {/* NEWS SOURCE TOGGLE */}
          {newsSource && handleNewsSourceToggle && (
            <Col xs={12} sm={12} md={6} lg={3} xl={3}>
              <Switch
                checked={newsSource === "fetched"}
                onChange={handleNewsSourceToggle}
                checkedChildren="RSS"
                unCheckedChildren="Report"
                size="default"
                style={{
                  backgroundColor:
                    newsSource === "fetched" ? "#ff4d4f" : undefined,
                  fontSize: screens.xs ? "11px" : "12px",
                }}
              />
            </Col>
          )}
        </Row>
      </Form>
    </Col>
  );
};
